import api from './axiosConfig';

// Interface for individual log book entry
export interface LogBookEntry {
    id: string;
    indigent_number: string;
    sa_id: string;
    erf_no: string;
    electricity_meter_no: string;
    account_number: string;
    name: string;
    surname: string;
    full_name: string;
    ward_no: string;
    services: string[];
    status: string;
    review_passed: boolean;
    approval_date: string;
    request_type: string;
    cell_number: string;
    occupants: Occupant[];
    financial_year: number;
}

// Interface for occupant data
export interface Occupant {
    name: string;
    surname: string;
    personal_id: string;
    employer_name?: string | null;
    monthly_income?: string | null;
    cell_number?: string | null;
    id: string;
    request_id: string;
    photo_path?: string;
}

// Interface for API response
export interface LogBookResponse {
    data: LogBookEntry[];
    total: number;
    page: number;
    limit: number;
    has_next: boolean;
    has_prev: boolean;
}

// Interface for query parameters
export interface LogBookFilters {
    ward_no?: string[] | null;
    financial_year?: number[] | null;
    status?: string[] | null;
    page?: number;
    limit?: number;
}

// Interface for export parameters
export interface ExportLogBookParams {
    ward_number?: string[] | null;
    financial_year?: number[] | null;
    status?: string[] | null;
    format: 'pdf' | 'csv' | 'excel';
}

export const logBookService = {
    // Get log book entries with filters
    getLogBookEntries: async (filters: LogBookFilters = {}): Promise<LogBookResponse> => {
        try {
            const params = new URLSearchParams();
            
            // Add filters to query parameters
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    if (Array.isArray(value)) {
                        // Handle array values (multi-select)
                        value.forEach(item => {
                            params.append(key, item.toString());
                        });
                    } else {
                        params.append(key, value.toString());
                    }
                }
            });

            const response = await api.get<LogBookResponse>(`/dashboard/indigent-register/?${params.toString()}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Export log book data
    exportLogBook: async (params: ExportLogBookParams): Promise<void> => {
        try {
            const queryParams = new URLSearchParams();

            // Add export parameters to query string
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    if (Array.isArray(value)) {
                        // Handle array values (multi-select)
                        value.forEach(item => {
                            queryParams.append(key, item.toString());
                        });
                    } else {
                        queryParams.append(key, value.toString());
                    }
                }
            });

            const response = await api.get(`/dashboard/indigent-register/export?${queryParams.toString()}`, {
                responseType: 'blob', // Important for file download
            });

            // Create a blob from the response data
            const blob = new Blob([response.data], {
                type: response.headers['content-type'] || 'application/octet-stream',
            });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Set filename based on format
            const timestamp = new Date().toISOString().split('T')[0];
            // Backend returns real .xlsx for excel/xls formats
            const ext = params.format.replace(/^(excel|xls)$/, 'xlsx');
            const filename = `logbook_export_${timestamp}.${ext}`;
            link.download = filename;

            // Trigger download
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            // Check if this is an AxiosError with response data
            if (error.response && error.response.data) {
                // For blob responses, we need to read the data as text to get the error message
                if (error.response.data instanceof Blob) {
                    const text = await error.response.data.text();
                    try {
                        const errorData = JSON.parse(text);
                        if (errorData.detail && errorData.detail.includes('No records found for export')) {
                            throw new Error('No records found for export');
                        }
                    } catch (parseError) {
                        // If parsing fails, throw the original error
                        throw error;
                    }
                } else {
                    // For regular JSON responses
                    if (error.response.data.detail && error.response.data.detail.includes('No records found for export')) {
                        throw new Error('No records found for export');
                    }
                }
            }
            throw error;
        }
    },

    // Helper method to format date for display
    formatDate: (dateString: string | null): string => {
        if (!dateString) {
            return '—';
        }
        
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            return '—';
        }
    },

    // Helper method to format services array for display
    formatServices: (services: string[]): string => {
        if (!services || services.length === 0) {
            return '—';
        }
        return services.join(', ');
    },

    // Helper method to get full name
    getFullName: (name: string, surname: string): string => {
        if (!name && !surname) {
            return '—';
        }
        return `${name || ''} ${surname || ''}`.trim();
    },

    // Helper method to format status for display
    formatStatus: (status: string): string => {
        if (!status) {
            return '—';
        }
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },

    // Helper method to map API status to UI status (similar to approvalService)
    mapStatusToUI: (apiStatus: string): 'Pending' | 'Approved' | 'Rejected' | 'Review Passed' | 'Review Failed' => {
        switch (apiStatus) {
            case 'pending':
                return 'Pending';
            case 'reviewed':
                return 'Review Passed';
            case 'approved':
                return 'Approved';
            case 'failed':
                return 'Review Failed';
            case 'rejected':
                return 'Rejected';
            default:
                return 'Pending';
        }
    },

    // Helper method to map UI status to API status
    mapUIToAPIStatus: (uiStatus: string): string => {
        switch (uiStatus) {
            case 'Pending':
                return 'pending';
            case 'Review Passed':
                return 'reviewed';
            case 'Approved':
                return 'approved';
            case 'Review Failed':
                return 'failed';
            case 'Rejected':
                return 'rejected';
            default:
                return 'pending';
        }
    }
}; 